import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Trash2, 
  Eye,
  LayoutGrid,
  LayoutList 
} from 'lucide-react';
import Header from '../components/layout/Header';
import MaterialReceiptModal from '../components/warehouse/MaterialReceiptModal';
import MaterialReceiptPreviewModal from '../components/warehouse/MaterialReceiptPreviewModal';
import { useAuth } from '../context/AuthContext';
import { materialReceipts } from '../services/api';
import ViewSwitch from '../utils/ViewSwitch';

// MaterialReceiptCard Component
const MaterialReceiptCard = ({ receipt, onPreview, onDelete, isAdmin, formatDate }) => {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            #{receipt.invoiceNumber}
          </h3>
          <p className="text-sm text-gray-500">{formatDate(receipt.date)}</p>
        </div>
        <div className="text-lg font-bold text-gray-900">
          ${receipt.totalAmount.toFixed(2)}
        </div>
      </div>

      {/* Warehouse Info */}
      <div>
        <div className="text-sm font-medium text-gray-600 mb-1">Warehouse</div>
        <p className="text-sm text-gray-900">{receipt.warehouse.name}</p>
      </div>

      {/* Currency Info */}
      <div className="flex justify-between text-sm text-gray-600">
        <span>Currency: {receipt.currency}</span>
        {receipt.currency !== 'USD' && (
          <span>Rate: {receipt.conversionRate.toFixed(4)}</span>
        )}
      </div>

      {/* Items */}
      <div>
        <div className="text-sm font-medium text-gray-600 mb-2">
          Items ({receipt.items.length})
        </div>
        <div className="space-y-1">
          {receipt.items.slice(0, 2).map((item, index) => (
            <div key={index} className="text-sm text-gray-700">
              <div className="flex justify-between">
                <span>
                  <span className="font-medium">{item.item.name}:</span>
                  {' '}{item.quantity} {item.item.amountType}
                </span>
                <span className="text-gray-600">
                  ${item.priceInDefaultCurrency.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
          {receipt.items.length > 2 && (
            <div className="text-xs text-blue-600">
              + {receipt.items.length - 2} more items
            </div>
          )}
        </div>
      </div>

      {/* Created Info */}
      <div className="text-xs text-gray-500">
        Created by: {receipt.createdBy?.name || 'Unknown'}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-3 border-t">
        <button
          onClick={() => onPreview(receipt._id)}
          className="p-2 text-blue-600 hover:text-blue-900 rounded-full hover:bg-blue-50"
          title="View Details"
        >
          <Eye className="h-5 w-5" />
        </button>
        
        {isAdmin && (
          <button
            onClick={() => onDelete(receipt._id)}
            className="p-2 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50"
            title="Delete Receipt"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Main Page Component
const MaterialReceiptPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCardView, setIsCardView] = useState(window.innerWidth < 768);
  const { user, isAdmin, isOfficeAdmin, isWarehouseAdmin } = useAuth();

  const canManageReceipts = isAdmin() || isOfficeAdmin() || isWarehouseAdmin();

  useEffect(() => {
    fetchReceipts();
    
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCardView(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchReceipts = async () => {
    try {
      const { data } = await materialReceipts.getAll();
      setReceipts(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load receipts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateReceipt = async (receiptData) => {
    try {
      await materialReceipts.create(receiptData);
      fetchReceipts();
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create receipt');
    }
  };

  const handleDeleteReceipt = async (id) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) {
      return;
    }

    try {
      await materialReceipts.delete(id);
      fetchReceipts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete receipt');
    }
  };

  const handlePreviewReceipt = async (id) => {
    try {
      const { data } = await materialReceipts.getById(id);
      setSelectedReceipt(data);
      setIsPreviewModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load receipt details');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReceipts = receipts.filter(receipt =>
    receipt.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    receipt.warehouse?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Material Receipts</h2>
            {canManageReceipts && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Receipt
              </button>
            )}
          </div>

          {/* Search and View Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search receipts..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ViewSwitch isCardView={isCardView} onViewChange={setIsCardView} />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {isCardView ? (
          // Card View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReceipts.map((receipt) => (
              <MaterialReceiptCard
                key={receipt._id}
                receipt={receipt}
                onPreview={handlePreviewReceipt}
                onDelete={handleDeleteReceipt}
                isAdmin={isAdmin()}
                formatDate={formatDate}
              />
            ))}
          </div>
        ) : (
          // Table View
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receipt ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Warehouse
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">#{receipt._id.slice(-8)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {receipt.invoiceNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(receipt.date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{receipt.warehouse.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {receipt.currency}
                          {receipt.currency !== 'USD' && (
                            <div className="text-xs text-gray-500">
                              Rate: {receipt.conversionRate.toFixed(4)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          ${receipt.totalAmount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handlePreviewReceipt(receipt._id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Receipt"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          {isAdmin() && (
                            <button
                              onClick={() => handleDeleteReceipt(receipt._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Receipt"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredReceipts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No receipts found matching your search criteria.</p>
          </div>
        )}

        {/* Modals */}
        {isModalOpen && (
          <MaterialReceiptModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedReceipt(null);
            }}
            onSave={handleCreateReceipt}
          />
        )}

        {isPreviewModalOpen && selectedReceipt && (
          <MaterialReceiptPreviewModal
            isOpen={isPreviewModalOpen}
            onClose={() => {
              setIsPreviewModalOpen(false);
              setSelectedReceipt(null);
            }}
            receipt={selectedReceipt}
          />
        )}
      </main>
    </div>
  );
};

export default MaterialReceiptPage;